import { supabase } from '@/integrations/supabase/client';

export interface FuncionarioPracticeProfile {
  id?: string;
  user_id: string;
  role_title: string | null;
  entity_type: string;
  practice_areas: string[];
  priority_norms: string[];
  response_style: string;
  detail_level: string;
  mandatory_warnings: string[];
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface FuncionarioPracticeProfileInput {
  role_title?: string | null;
  entity_type?: string;
  practice_areas?: string[];
  priority_norms?: string[];
  response_style?: string;
  detail_level?: string;
  mandatory_warnings?: string[];
  notes?: string | null;
}

export interface PracticeContextBase {
  municipality_name?: string | null;
  autonomous_community?: string | null;
  user_type?: string | null;
}

export const DEFAULT_PRACTICE_PROFILE: FuncionarioPracticeProfileInput = {
  role_title: '',
  entity_type: 'ayuntamiento',
  practice_areas: [],
  priority_norms: [],
  response_style: 'practico',
  detail_level: 'medio',
  mandatory_warnings: [],
  notes: '',
};

export async function getFuncionarioPracticeProfile(userId: string): Promise<FuncionarioPracticeProfile | null> {
  const { data, error } = await (supabase as any)
    .from('funcionario_practice_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching funcionario practice profile:', error);
    throw error;
  }

  return data as FuncionarioPracticeProfile | null;
}

export async function upsertFuncionarioPracticeProfile(
  userId: string,
  input: FuncionarioPracticeProfileInput
): Promise<FuncionarioPracticeProfile> {
  const payload = {
    user_id: userId,
    role_title: input.role_title?.trim() || null,
    entity_type: input.entity_type || 'ayuntamiento',
    practice_areas: input.practice_areas || [],
    priority_norms: input.priority_norms || [],
    response_style: input.response_style || 'practico',
    detail_level: input.detail_level || 'medio',
    mandatory_warnings: input.mandatory_warnings || [],
    notes: input.notes?.trim() || null,
  };

  const { data, error } = await (supabase as any)
    .from('funcionario_practice_profiles')
    .upsert(payload, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) {
    console.error('Error saving funcionario practice profile:', error);
    throw error;
  }

  return data as FuncionarioPracticeProfile;
}

export function splitProfileList(value: string): string[] {
  return value
    .split(/[\n,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function joinProfileList(value?: string[] | null): string {
  return (value || []).join('\n');
}

export function buildFuncionarioPracticeContext(
  profile: FuncionarioPracticeProfile | FuncionarioPracticeProfileInput | null,
  base: PracticeContextBase = {}
): string {
  const lines: string[] = [];

  if (base.municipality_name || base.autonomous_community || base.user_type) {
    lines.push('Contexto institucional:');
    if (base.municipality_name) lines.push(`- Entidad/municipio: ${base.municipality_name}`);
    if (base.autonomous_community) lines.push(`- Comunidad autonoma: ${base.autonomous_community}`);
    if (base.user_type) lines.push(`- Tipo de usuario: ${base.user_type}`);
  }

  if (!profile) {
    return lines.length > 0
      ? ['PERFIL DE PRACTICA DEL FUNCIONARIO', ...lines].join('\n')
      : '';
  }

  const practiceAreas = profile.practice_areas || [];
  const priorityNorms = profile.priority_norms || [];
  const mandatoryWarnings = profile.mandatory_warnings || [];

  if (profile.role_title) lines.push(`- Puesto: ${profile.role_title}`);
  if (profile.entity_type) lines.push(`- Tipo de entidad: ${profile.entity_type}`);
  if (practiceAreas.length) lines.push(`- Areas habituales: ${practiceAreas.join(', ')}`);
  if (priorityNorms.length) lines.push(`- Normativa prioritaria: ${priorityNorms.join(', ')}`);
  if (profile.response_style) lines.push(`- Estilo preferido: ${profile.response_style}`);
  if (profile.detail_level) lines.push(`- Nivel de detalle: ${profile.detail_level}`);
  if (mandatoryWarnings.length) lines.push(`- Advertencias obligatorias: ${mandatoryWarnings.join('; ')}`);
  if (profile.notes) lines.push(`- Notas internas: ${profile.notes}`);

  return lines.length > 0
    ? ['PERFIL DE PRACTICA DEL FUNCIONARIO', ...lines].join('\n')
    : '';
}
